
import React, { useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import DetailPageTemplate from "@/components/DetailPageTemplate";
import { LoadingState, ErrorState } from "@/components/notifications/detail/NotificationDetailStates";
import { useNotificationDetail } from "@/hooks/useNotificationDetail";
import { useNotificationMutations } from "@/hooks/useNotificationMutations";
import { useNotificationReadStatus } from "@/hooks/useNotificationReadStatus";
import { useNotificationPermissions } from "@/hooks/notifications/useNotificationPermissions";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import NotificationDetailContainer from "@/components/notifications/detail/NotificationDetailContainer";

const NotificationDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { canEdit, canDelete, checkEditPermission, checkDeletePermission } = useNotificationPermissions();
  const { user } = useAuth(); // Get the user directly from AuthContext
  const { markAsRead } = useNotificationReadStatus();
  
  const {
    notification,
    editedNotification,
    editMode,
    setEditMode,
    isAnalyzing,
    setIsAnalyzing,
    showEditHistory,
    setShowEditHistory,
    isLoading,
    error,
    editHistory,
    isLoadingHistory,
    handleInputChange,
    handleAIAnalysisComplete,
    handleSubmit,
    resetNotification,
    updateNotification,
    refetch
  } = useNotificationDetail(id);

  const {
    archiveNotification,
    restoreNotification,
    deleteNotification,
    user: mutationUser
  } = useNotificationMutations(id);

  useEffect(() => {
    if (id && notification) {
      markAsRead.mutate(id);
    }
  }, [id, notification]);

  // Get the back URL with all search parameters preserved
  const getBackUrl = () => {
    // Build path with all current search parameters
    const searchParams = new URLSearchParams(location.search);
    
    // Ensure the 'tab' parameter is set to 'view' to return to the notification list tab
    if (!searchParams.has('tab')) {
      searchParams.set('tab', 'view');
    }
    
    return `/notifications?${searchParams.toString()}`;
  };

  const handleDelete = () => {
    if (!checkDeletePermission()) {
      return;
    }
    
    if (confirm('Weet je zeker dat je deze melding wilt verwijderen? Dit kan niet ongedaan worden gemaakt.')) {
      deleteNotification.mutate();
    }
  };

  const handleArchive = () => {
    if (!checkEditPermission()) {
      return;
    }
    archiveNotification.mutate();
  };

  const handleRestore = () => {
    if (!checkEditPermission()) {
      return;
    }
    restoreNotification.mutate();
  };

  const handleBack = () => {
    navigate(getBackUrl());
  };

  if (isLoading) {
    return <LoadingState />;
  }

  if (error || !notification) {
    return <ErrorState error={error as Error} />;
  }

  return (
    <DetailPageTemplate
      title={`Melding: ${notification.vo_number || 'Geen VO nummer'}`}
      backButtonProps={{
        label: "Terug naar overzicht",
        onClick: handleBack
      }}
    >
      <div className="grid gap-6">
        <NotificationDetailContainer
          notification={notification}
          editedNotification={editedNotification}
          editMode={editMode}
          setEditMode={setEditMode}
          isAnalyzing={isAnalyzing}
          setIsAnalyzing={setIsAnalyzing}
          showEditHistory={showEditHistory}
          setShowEditHistory={setShowEditHistory}
          editHistory={editHistory}
          isLoadingHistory={isLoadingHistory}
          handleInputChange={handleInputChange}
          handleAIAnalysisComplete={handleAIAnalysisComplete}
          handleSubmit={handleSubmit}
          resetNotification={resetNotification}
          updateNotification={updateNotification}
          canEdit={canEdit}
          canDelete={canDelete}
          onArchive={handleArchive}
          onRestore={handleRestore}
          onDelete={handleDelete}
          restorePending={restoreNotification.isPending}
          archivePending={archiveNotification.isPending}
          deletePending={deleteNotification.isPending}
          user={user} // Pass the current user from AuthContext
          onBack={handleBack}
        />
      </div>
    </DetailPageTemplate>
  );
};

export default NotificationDetail;
